import {
  ProjectCreateCommandSchema,
  ProjectProjectionSchema,
  ProjectListSchema,
  RankCommandSchema,
  RankResultSchema,
  TaskActivityListSchema,
  TaskCommentCreateCommandSchema,
  TaskCommentListSchema,
  TaskCommentProjectionSchema,
  TaskCreateCommandSchema,
  TaskPageQuerySchema,
  TaskScopedListSchema,
  TaskBoardItemSchema,
  TaskListQuerySchema,
  TaskUpdateCommandSchema,
  TaskUpdateResultSchema,
  UuidSchema,
  type ProjectCreateCommand,
  type RankCommand,
  type TaskCommentCreateCommand,
  type TaskCreateCommand,
  type TaskPageQuery,
  type TaskUpdateCommand,
} from "@mind/contracts/browser";
import { apiRequest, type ApiRequestOptions } from "../../api/client.js";

function pathWithQuery(
  path: string,
  parameters: Readonly<Record<string, string | number | boolean | null | undefined>>,
): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(parameters)) {
    if (value !== null && value !== undefined) query.set(key, String(value));
  }
  const encoded = query.toString();
  return encoded.length === 0 ? path : `${path}?${encoded}`;
}

function withOptionalSignal(options: ApiRequestOptions, signal?: AbortSignal): ApiRequestOptions {
  return signal === undefined ? options : { ...options, signal };
}

export type WorkTaskListParams = {
  readonly workspaceId?: string;
  readonly projectId?: string;
  readonly assignee?: "me";
  readonly includeArchived?: boolean;
  readonly cursor?: string | null;
  readonly limit?: number;
};

export const workApi = {
  listProjects(workspaceId: string, includeArchived = false, signal?: AbortSignal) {
    const parsedWorkspaceId = UuidSchema.parse(workspaceId);
    return apiRequest(
      ProjectListSchema,
      pathWithQuery("/api/projects", { workspaceId: parsedWorkspaceId, includeArchived }),
      withOptionalSignal({}, signal),
    );
  },

  getProject(projectId: string, signal?: AbortSignal) {
    const parsedProjectId = UuidSchema.parse(projectId);
    return apiRequest(
      ProjectProjectionSchema,
      `/api/projects/${encodeURIComponent(parsedProjectId)}`,
      withOptionalSignal({}, signal),
    );
  },

  createProject(input: ProjectCreateCommand) {
    return apiRequest(ProjectProjectionSchema, "/api/projects", {
      method: "POST",
      body: ProjectCreateCommandSchema.parse(input),
    });
  },

  listTasks(params: WorkTaskListParams, signal?: AbortSignal) {
    const input = TaskListQuerySchema.parse({
      workspaceId: params.workspaceId ?? null,
      projectId: params.projectId ?? null,
      assignee: params.assignee ?? null,
      includeArchived: params.includeArchived ?? false,
      cursor: params.cursor ?? null,
      limit: params.limit ?? 250,
    });
    return apiRequest(
      TaskScopedListSchema,
      pathWithQuery("/api/tasks", {
        workspaceId: input.workspaceId,
        projectId: input.projectId,
        assignee: input.assignee,
        includeArchived: input.includeArchived,
        cursor: input.cursor,
        limit: input.limit,
      }),
      withOptionalSignal({}, signal),
    );
  },

  getTask(taskId: string, signal?: AbortSignal) {
    const parsedTaskId = UuidSchema.parse(taskId);
    return apiRequest(
      TaskBoardItemSchema,
      `/api/tasks/${encodeURIComponent(parsedTaskId)}`,
      withOptionalSignal({}, signal),
    );
  },

  createTask(input: TaskCreateCommand) {
    return apiRequest(RankResultSchema, "/api/tasks", {
      method: "POST",
      body: TaskCreateCommandSchema.parse(input),
    });
  },

  updateTask(taskId: string, input: Omit<TaskUpdateCommand, "taskId">) {
    const parsedTaskId = UuidSchema.parse(taskId);
    return apiRequest(TaskUpdateResultSchema, `/api/tasks/${encodeURIComponent(parsedTaskId)}`, {
      method: "PATCH",
      body: TaskUpdateCommandSchema.parse({ ...input, taskId: parsedTaskId }),
    });
  },

  rankTask(input: RankCommand) {
    return apiRequest(RankResultSchema, "/api/task-commands", {
      method: "POST",
      body: RankCommandSchema.parse(input),
    });
  },

  listComments(taskId: string, params: TaskPageQuery, signal?: AbortSignal) {
    const parsedTaskId = UuidSchema.parse(taskId);
    const query = TaskPageQuerySchema.parse(params);
    return apiRequest(
      TaskCommentListSchema,
      pathWithQuery(`/api/tasks/${encodeURIComponent(parsedTaskId)}/comments`, query),
      withOptionalSignal({}, signal),
    );
  },

  createComment(taskId: string, input: Omit<TaskCommentCreateCommand, "taskId">) {
    const parsedTaskId = UuidSchema.parse(taskId);
    return apiRequest(TaskCommentProjectionSchema, `/api/tasks/${encodeURIComponent(parsedTaskId)}/comments`, {
      method: "POST",
      body: TaskCommentCreateCommandSchema.parse({ ...input, taskId: parsedTaskId }),
    });
  },

  listActivity(taskId: string, params: TaskPageQuery, signal?: AbortSignal) {
    const parsedTaskId = UuidSchema.parse(taskId);
    const query = TaskPageQuerySchema.parse(params);
    return apiRequest(
      TaskActivityListSchema,
      pathWithQuery(`/api/tasks/${encodeURIComponent(parsedTaskId)}/activity`, query),
      withOptionalSignal({}, signal),
    );
  },
};

export function createProjectCommand(
  workspaceId: string,
  name: string,
  timezone: string,
  commandId = crypto.randomUUID(),
  projectId = crypto.randomUUID(),
): ProjectCreateCommand {
  return ProjectCreateCommandSchema.parse({ commandId, projectId, workspaceId, name, timezone });
}

export function createTaskCommand(
  input: Omit<TaskCreateCommand, "commandId" | "taskId">,
  commandId = crypto.randomUUID(),
  taskId = crypto.randomUUID(),
): TaskCreateCommand {
  return TaskCreateCommandSchema.parse({ ...input, commandId, taskId });
}

export function createTaskUpdateCommand(
  taskId: string,
  revision: string,
  changes: Omit<TaskUpdateCommand, "commandId" | "taskId" | "revision">,
  commandId = crypto.randomUUID(),
): TaskUpdateCommand {
  return TaskUpdateCommandSchema.parse({ ...changes, commandId, taskId, revision });
}

export function createRankCommand(
  input: Omit<RankCommand, "commandId">,
  commandId = crypto.randomUUID(),
): RankCommand {
  return RankCommandSchema.parse({ ...input, commandId });
}
